import { writable, get } from "svelte/store";
import { section } from "./section.js";

/*
 * Hash-based router.
 *
 * The URL hash is the source of truth for the current view, e.g.:
 *   #/dashboard
 *   #/users            #/users/:userId
 *   #/workspaces       #/workspaces/:workspaceId
 *   #/admin
 *   #/billing
 *
 * It stays bidirectionally in sync with the existing `section` store so the
 * rest of the app (App.svelte rendering, Navbar set_section, route-protection
 * redirects) keeps working exactly as before — those still read/write the
 * section store; this module mirrors that into the URL and vice-versa.
 *
 * `#agent` is handled separately in main.js (a different root component) and is
 * intentionally NOT a router path.
 */

const SECTION_TO_PATH = {
  dashboard: "dashboard",
  user_list_v2: "users",
  workspaces: "workspaces",
  admin_users: "admin",
  billing_plans: "billing",
  // Retired/hidden sections keep a path so old links still resolve; App.svelte
  // redirects them to a visible section.
  user_list: "users-legacy",
  upgrades: "upgrades",
  promo: "promo",
  push: "push",
};

const PATH_TO_SECTION = Object.fromEntries(
  Object.entries(SECTION_TO_PATH).map(([sec, path]) => [path, sec])
);

// Paths that carry a trailing detail id, and the param name it maps to.
const PATH_DETAIL_PARAM = {
  users: "userId",
  workspaces: "workspaceId",
};

function buildHash(sectionName, params = {}) {
  const path = SECTION_TO_PATH[sectionName] || "dashboard";
  const paramKey = PATH_DETAIL_PARAM[path];
  const id = paramKey ? params[paramKey] : null;
  return "#/" + path + (id ? "/" + encodeURIComponent(String(id)) : "");
}

function parseHash() {
  const raw = (window.location.hash || "").replace(/^#\/?/, "");
  const [path, rawId] = raw.split("/");
  const sec = PATH_TO_SECTION[path];
  if (!sec) return { section: "dashboard", params: {}, path: "dashboard" };
  const params = {};
  const paramKey = PATH_DETAIL_PARAM[path];
  if (paramKey && rawId) params[paramKey] = decodeURIComponent(rawId);
  return { section: sec, params, path };
}

export const route = writable(parseHash());

let syncing = false; // guards the section <-> URL echo so we don't loop
let ready = false; // becomes true after initial sync is established

/**
 * Programmatic navigation. Updates the URL hash (adding a browser history
 * entry), which flows back through the hashchange listener to update stores.
 */
export function navigate(sectionName, params = {}) {
  const hash = buildHash(sectionName, params);
  if (window.location.hash === hash) {
    // Same hash => no hashchange event fires; update stores directly.
    route.set(parseHash());
    syncing = true;
    section.set(sectionName);
    syncing = false;
  } else {
    window.location.hash = hash;
  }
}

// `#agent` is owned by main.js (it swaps the whole root component). The router
// must never parse or rewrite it, or it would clobber the hash before main.js
// can reload into the dev tool.
const isAgentHash = () => window.location.hash === "#agent";

function onHash() {
  if (isAgentHash()) return;
  const r = parseHash();
  route.set(r);
  syncing = true;
  section.set(r.section);
  syncing = false;
}

// Legacy writes to the section store (Navbar's set_section, App's redirects)
// should also update the URL. A plain section change drops any detail id —
// navigating to a list closes an open detail, which matches prior behavior.
section.subscribe((sec) => {
  if (!ready || syncing || isAgentHash()) return;
  const hash = buildHash(sec, {});
  if (window.location.hash !== hash) window.location.hash = hash;
  else route.set(parseHash());
});

window.addEventListener("hashchange", onHash);

// --- Initial sync: a valid route hash wins; otherwise seed the URL from the
// current section store (its localStorage-backed default), preserving the
// prior "land where you left off" behavior while making the view linkable.
(function init() {
  if (isAgentHash()) return; // #agent is main.js's territory
  const path = (window.location.hash || "").replace(/^#\/?/, "").split("/")[0];
  if (path && PATH_TO_SECTION[path]) {
    onHash();
  } else {
    const sec = get(section);
    window.location.hash = buildHash(sec, {});
    route.set(parseHash());
  }
  ready = true;
})();
